import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  createChecklistSubmission,
  createSubmissionResponses,
  fetchActiveChecklistsForOperator,
  fetchChecklistItems,
  fetchUserRecentSubmissions,
} from "@/modules/checklist/api";
import type { FieldSnapshotInput } from "@/modules/checklist/api/types";
import { useAuth } from "@/core/AuthContext";
import { useLanguage } from "@/core/LanguageContext";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { Textarea } from "@/shared/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/shared/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Badge } from "@/shared/ui/badge";
import { Progress } from "@/shared/ui/progress";
import { ClipboardCheck, CheckCircle, XCircle, ArrowLeft, Clock, CheckCircle2, Activity, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

type ItemType = "yes_no" | "text" | "number" | "photo" | "dropdown" | "field_lookup";

interface ItemFieldMapping {
  mapping_id: string;
  display_name: string;
  unit: string | null;
  display_order: number;
}

interface ChecklistItem {
  id: string;
  item_type: ItemType;
  label: string;
  description: string | null;
  is_required: boolean;
  is_inspection: boolean;
  dropdown_options: string[] | null;
  sort_order: number;
  label_translations: Record<string, string> | null;
  description_translations: Record<string, string> | null;
  field_mappings: ItemFieldMapping[];
}

interface FieldValueEntry {
  mapping_id: string;
  display_name: string;
  value: string;
  unit: string | null;
  loading: boolean;
}

interface FactoryArea {
  id: string;
  name: string;
  color: string | null;
}

interface ProductionLine {
  id: string;
  name: string;
}

interface Station {
  id: string;
  name: string;
}

interface Checklist {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  factory_id: string;
  area: FactoryArea | null;
  line: ProductionLine | null;
  station: Station | null;
  checklist_items: ChecklistItem[];
}

interface SubmissionResponse {
  item_id: string;
  response_value: string | null;
  is_passed: boolean | null;
}

interface OperatorViewProps {
  factoryId: string;
  tenantId: string | null;
}

export function OperatorView({ factoryId, tenantId }: OperatorViewProps) {
  const { user } = useAuth();
  const { getTranslation } = useLanguage();
  const queryClient = useQueryClient();
  const [selectedChecklist, setSelectedChecklist] = useState<Checklist | null>(null);
  const [responses, setResponses] = useState<Map<string, SubmissionResponse>>(new Map());
  const [notes, setNotes] = useState("");
  const [fieldValues, setFieldValues] = useState<Map<string, FieldValueEntry[]>>(new Map());

  const { data: checklists = [], isLoading } = useQuery({
    queryKey: ["active-checklists", tenantId, factoryId],
    queryFn: async () => {
      if (!tenantId) return [];

      const templates = await fetchActiveChecklistsForOperator(tenantId, factoryId);

      const withItems = await Promise.all(
        templates.map(async (template) => {
          const items = await fetchChecklistItems(tenantId, template.id);

          const checklistItems = items
            .map((item) => {
              const mappings: ItemFieldMapping[] = (item.field_mappings ?? [])
                .sort((a, b) => a.display_order - b.display_order)
                .map((fm) => ({
                  mapping_id: fm.field_mapping_id,
                  display_name: fm.field_mapping?.datapoint?.display_name || "Field Value",
                  unit: fm.field_mapping?.datapoint?.unit?.symbol || null,
                  display_order: fm.display_order,
                }));

              return {
                id: item.id,
                item_type: item.item_type as ItemType,
                label: item.label,
                description: item.description,
                is_required: item.is_required ?? false,
                is_inspection: item.is_inspection ?? false,
                dropdown_options: Array.isArray(item.dropdown_options)
                  ? (item.dropdown_options as string[])
                  : null,
                sort_order: item.sort_order,
                label_translations: item.label_translations as Record<string, string> | null,
                description_translations: item.description_translations as Record<string, string> | null,
                field_mappings: mappings,
              } satisfies ChecklistItem;
            })
            .sort((a, b) => a.sort_order - b.sort_order);

          return {
            id: template.id,
            tenant_id: template.tenant_id,
            name: template.name,
            description: template.description,
            factory_id: template.factory_id,
            area: template.area ?? null,
            line: template.line ?? null,
            station: template.station ?? null,
            checklist_items: checklistItems,
          } as Checklist;
        }),
      );

      return withItems;
    },
    enabled: !!tenantId && !!factoryId,
  });

  // Fetch real-time field values for field_lookup items
  const fetchFieldValues = useCallback(async (checklist: Checklist) => {
    const fieldLookupItems = checklist.checklist_items.filter(
      item => item.item_type === "field_lookup" && item.field_mappings.length > 0
    );

    if (fieldLookupItems.length === 0) return;

    // Set loading state for all mappings
    setFieldValues((previous) => {
      const next = new Map(previous);
      for (const item of fieldLookupItems) {
        next.set(
          item.id,
          item.field_mappings.map((fm) => ({
            mapping_id: fm.mapping_id,
            display_name: fm.display_name,
            value: "",
            unit: fm.unit,
            loading: true,
          })),
        );
      }
      return next;
    });

    try {
      // Collect all unique mapping IDs across all items
      const allMappingIds = [
        ...new Set(fieldLookupItems.flatMap(item => item.field_mappings.map(fm => fm.mapping_id))),
      ];

      const { data, error } = await supabase.functions.invoke("influxdb-query", {
        body: { 
          factory_id: checklist.factory_id,
          mapping_ids: allMappingIds 
        },
      });

      if (error) throw error;

      const resultsData = (data?.data || {}) as Record<string, { value?: unknown; timestamp?: string }>;

      // Build per-item value arrays and set responses
      setFieldValues((previous) => {
        const next = new Map(previous);
        for (const item of fieldLookupItems) {
          next.set(
            item.id,
            item.field_mappings.map((fm) => {
              const result = resultsData[fm.mapping_id];
              const value = result?.value !== undefined && result?.value !== null
                ? String(result.value)
                : "N/A";
              return {
                mapping_id: fm.mapping_id,
                display_name: fm.display_name,
                value,
                unit: fm.unit,
                loading: false,
              };
            }),
          );
        }
        return next;
      });

      // Auto-populate response values with a summary string
      setResponses((previous) => {
        const next = new Map(previous);
        for (const item of fieldLookupItems) {
          const values = item.field_mappings.map((fm) => {
            const result = resultsData[fm.mapping_id];
            return result?.value !== undefined && result?.value !== null
              ? String(result.value)
              : null;
          });
          const hasAnyValue = values.some(v => v !== null);
          if (hasAnyValue) {
            // Build a human-readable summary as response_value
            const summary = item.field_mappings
              .map((fm, i) => `${fm.display_name}: ${values[i] ?? "N/A"}${fm.unit ? ` ${fm.unit}` : ""}`)
              .join("; ");
            next.set(item.id, {
              item_id: item.id,
              response_value: summary,
              is_passed: null,
            });
          }
        }
        return next;
      });
    } catch (error) {
      console.error("Failed to fetch field values:", error);
      setFieldValues((previous) => {
        const next = new Map(previous);
        for (const item of fieldLookupItems) {
          next.set(
            item.id,
            item.field_mappings.map((fm) => ({
              mapping_id: fm.mapping_id,
              display_name: fm.display_name,
              value: "Error",
              unit: null,
              loading: false,
            })),
          );
        }
        return next;
      });
    }
  }, []);

  // Fetch field values when a checklist is selected
  useEffect(() => {
    if (selectedChecklist) {
      fetchFieldValues(selectedChecklist);
    }
  }, [selectedChecklist, fetchFieldValues]);

  const refreshFieldValues = () => {
    if (selectedChecklist) {
      fetchFieldValues(selectedChecklist);
    }
  };

  const getAssignmentLabel = (checklist: Checklist): { label: string; color?: string | null } => {
    if (checklist.station) {
      return { label: `Station: ${checklist.station.name}` };
    }
    if (checklist.line) {
      return { label: `Line: ${checklist.line.name}` };
    }
    if (checklist.area) {
      return { label: checklist.area.name, color: checklist.area.color };
    }
    return { label: "Factory-wide" };
  };

  const { data: recentSubmissions = [] } = useQuery({
    queryKey: ["recent-submissions", tenantId, factoryId, user?.id],
    queryFn: async () => {
      if (!user || !tenantId) return [];
      return fetchUserRecentSubmissions(tenantId, user.id, 5);
    },
    enabled: !!user && !!tenantId,
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      if (!user || !selectedChecklist) throw new Error("Missing data");

      // Check required items
      const requiredItems = selectedChecklist.checklist_items.filter(i => i.is_required);
      for (const item of requiredItems) {
        const response = responses.get(item.id);
        if (!response) {
          throw new Error(`Please complete: ${item.label}`);
        }
        // Instruction items just need to be marked complete
        if (!item.is_inspection) {
          if (!response.response_value) {
            throw new Error(`Please complete: ${item.label}`);
          }
        } else if (item.item_type === "yes_no" && response.is_passed === null) {
          throw new Error(`Please complete: ${item.label}`);
        } else if (item.item_type !== "yes_no" && !response.response_value?.trim()) {
          throw new Error(`Please complete: ${item.label}`);
        }
      }

      // Check for any failures
      const hasFailed = Array.from(responses.values()).some(r => r.is_passed === false);
      const status = hasFailed ? "failed" : "completed";

      const submission = await createChecklistSubmission({
        tenant_id: selectedChecklist.tenant_id,
        checklist_id: selectedChecklist.id,
        submitted_by: user.id,
        status,
        notes: notes || null,
        completed_at: new Date().toISOString(),
      });

      // Insert responses — build structured payload + snapshots for field_lookup items
      const itemMap = new Map(
        selectedChecklist.checklist_items.map(i => [i.id, i]),
      );

      const responseData = Array.from(responses.entries()).map(([itemId, resp]) => {
        const item = itemMap.get(itemId);
        const isFieldLookup = item?.item_type === "field_lookup";
        const itemFvs = isFieldLookup ? (fieldValues.get(itemId) ?? []) : [];

        // Build structured response_payload for field_lookup items
        const response_payload = isFieldLookup && itemFvs.length > 0
          ? {
              type: "field_lookup" as const,
              field_values: itemFvs.map(fv => ({
                mapping_id: fv.mapping_id,
                display_name: fv.display_name,
                value: fv.value,
                unit: fv.unit,
              })),
            }
          : undefined;

        // Build snapshot inputs for field_lookup items
        const field_snapshots: FieldSnapshotInput[] | undefined =
          isFieldLookup && itemFvs.length > 0
            ? itemFvs.map(fv => ({
                mapping_id: fv.mapping_id,
                display_name: fv.display_name,
                value: fv.value === "N/A" || fv.value === "Error" ? null : fv.value,
                unit: fv.unit,
                capture_status: fv.value === "Error"
                  ? "query_error" as const
                  : fv.value === "N/A"
                    ? "missing_mapping" as const
                    : "ok" as const,
              }))
            : undefined;

        return {
          tenant_id: selectedChecklist.tenant_id,
          submission_id: submission.id,
          item_id: itemId,
          response_value: resp.response_value,
          is_passed: resp.is_passed,
          response_payload,
          field_snapshots,
        };
      });

      if (responseData.length > 0) {
        await createSubmissionResponses(responseData);
      }

      return { status };
    },
    onSuccess: ({ status }) => {
      queryClient.invalidateQueries({ queryKey: ["recent-submissions"] });
      toast.success(status === "completed" ? "Work instruction completed successfully!" : "Work instruction submitted with issues");
      setSelectedChecklist(null);
      setResponses(new Map());
      setFieldValues(new Map());
      setNotes("");
    },
    onError: (error) => toast.error(error.message),
  });

  const updateResponse = (itemId: string, update: Partial<SubmissionResponse>) => {
    setResponses(prev => {
      const newMap = new Map(prev);
      const existing = newMap.get(itemId) || { item_id: itemId, response_value: null, is_passed: null };
      newMap.set(itemId, { ...existing, ...update });
      return newMap;
    });
  };

  const getCompletionProgress = () => {
    if (!selectedChecklist) return 0;
    const total = selectedChecklist.checklist_items.length;
    if (total === 0) return 100;
    let completed = 0;
    for (const item of selectedChecklist.checklist_items) {
      const resp = responses.get(item.id);
      if (resp) {
        // Instruction items just need response_value set (timestamp)
        if (!item.is_inspection) {
          if (resp.response_value) completed++;
        } else if (item.item_type === "yes_no" && resp.is_passed !== null) {
          completed++;
        } else if (item.item_type !== "yes_no" && resp.response_value?.trim()) {
          completed++;
        }
      }
    }
    return Math.round((completed / total) * 100);
  };

  if (isLoading) {
    return <div className="text-muted-foreground">Loading work instructions...</div>;
  }

  if (selectedChecklist) {
    const progress = getCompletionProgress();
    
    return (
      <div className="space-y-4 md:space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => { setSelectedChecklist(null); setResponses(new Map()); setFieldValues(new Map()); setNotes(""); }}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">{selectedChecklist.name}</h2>
            <Badge 
              variant="outline"
              style={{ 
                borderColor: getAssignmentLabel(selectedChecklist).color || undefined,
                color: getAssignmentLabel(selectedChecklist).color || undefined
              }}
            >
              {getAssignmentLabel(selectedChecklist).label}
            </Badge>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{progress}%</span>
          </div>
          <Progress value={progress} />
        </div>

        <div className="space-y-4">
          {selectedChecklist.checklist_items.map((item, index) => (
            <Card key={item.id}>
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <span className="text-sm font-medium text-muted-foreground w-6 shrink-0">
                    {index + 1}.
                  </span>
                  <div className="flex-1 space-y-3">
                    <div>
                      <p className="font-medium">
                        {getTranslation(item.label_translations, item.label)}
                        {item.is_required && <span className="text-destructive ml-1">*</span>}
                      </p>
                      {(item.description || item.description_translations) && (
                        <p className="text-sm text-muted-foreground">
                          {getTranslation(item.description_translations, item.description || "")}
                        </p>
                      )}
                    </div>

                    {/* Instruction item - simple completion button */}
                    {!item.is_inspection && (
                      <Button
                        variant={responses.get(item.id)?.response_value ? "default" : "outline"}
                        className="gap-2"
                        onClick={() => {
                          if (!responses.get(item.id)?.response_value) {
                            updateResponse(item.id, { response_value: new Date().toISOString() });
                          } else {
                            updateResponse(item.id, { response_value: null });
                          }
                        }}
                      >
                        <CheckCircle className="w-4 h-4" />
                        {responses.get(item.id)?.response_value ? "Completed" : "Mark Complete"}
                      </Button>
                    )}

                    {/* Inspection items - show input fields based on type */}
                    {item.is_inspection && item.item_type === "yes_no" && (
                      <div className="flex gap-2">
                        <Button
                          variant={responses.get(item.id)?.is_passed === true ? "default" : "outline"}
                          className="gap-2"
                          onClick={() => updateResponse(item.id, { is_passed: true })}
                        >
                          <CheckCircle className="w-4 h-4" />
                          Yes / Pass
                        </Button>
                        <Button
                          variant={responses.get(item.id)?.is_passed === false ? "destructive" : "outline"}
                          className="gap-2"
                          onClick={() => updateResponse(item.id, { is_passed: false })}
                        >
                          <XCircle className="w-4 h-4" />
                          No / Fail
                        </Button>
                      </div>
                    )}

                    {item.is_inspection && item.item_type === "text" && (
                      <Textarea
                        placeholder="Enter your response..."
                        value={responses.get(item.id)?.response_value || ""}
                        onChange={(e) => updateResponse(item.id, { response_value: e.target.value })}
                      />
                    )}

                    {item.is_inspection && item.item_type === "number" && (
                      <Input
                        type="number"
                        placeholder="Enter value..."
                        value={responses.get(item.id)?.response_value || ""}
                        onChange={(e) => updateResponse(item.id, { response_value: e.target.value })}
                      />
                    )}

                    {item.is_inspection && item.item_type === "dropdown" && item.dropdown_options && (
                      <Select
                        value={responses.get(item.id)?.response_value || ""}
                        onValueChange={(v) => updateResponse(item.id, { response_value: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select an option..." />
                        </SelectTrigger>
                        <SelectContent>
                          {item.dropdown_options.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}

                    {item.is_inspection && item.item_type === "photo" && (
                      <div className="text-sm text-muted-foreground">
                        Photo upload coming soon
                      </div>
                    )}

                    {item.is_inspection && item.item_type === "field_lookup" && (
                      <div className="space-y-2">
                        {(fieldValues.get(item.id) ?? []).length > 0 ? (
                          (fieldValues.get(item.id) ?? []).map((fv) => (
                            <div key={fv.mapping_id} className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                              <Activity className="w-5 h-5 text-primary shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-muted-foreground truncate">
                                  {fv.display_name}
                                </p>
                                {fv.loading ? (
                                  <div className="flex items-center gap-2">
                                    <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">Loading...</span>
                                  </div>
                                ) : (
                                  <p className="text-xl font-bold">
                                    {fv.value || "N/A"}
                                    {fv.unit && (
                                      <span className="text-sm font-normal text-muted-foreground ml-1">
                                        {fv.unit}
                                      </span>
                                    )}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))
                        ) : item.field_mappings.length > 0 ? (
                          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                            <Activity className="w-5 h-5 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">
                              {item.field_mappings.length} field{item.field_mappings.length > 1 ? "s" : ""} configured
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground italic">No field mappings configured</p>
                        )}
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-muted-foreground">
                            {item.field_mappings.length > 1 ? "These values" : "This value"} will be recorded when you submit.
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="gap-1 h-7 text-xs"
                            onClick={refreshFieldValues}
                            disabled={(fieldValues.get(item.id) ?? []).some(fv => fv.loading)}
                          >
                            <RefreshCw className={`w-3 h-3 ${(fieldValues.get(item.id) ?? []).some(fv => fv.loading) ? "animate-spin" : ""}`} />
                            Refresh
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardContent className="pt-4">
            <label className="text-sm font-medium">Additional Notes</label>
            <Textarea
              className="mt-2"
              placeholder="Any issues or comments..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </CardContent>
        </Card>

        <Button 
          className="w-full" 
          size="lg" 
          onClick={() => submitMutation.mutate()}
          disabled={submitMutation.isPending}
        >
          {submitMutation.isPending ? "Submitting..." : "Submit Work Instruction"}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Available Work Instructions</h2>
        <p className="text-sm text-muted-foreground">Select a work instruction to complete for this shift</p>
      </div>

      {checklists.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardCheck className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-1">No work instructions available</h3>
            <p className="text-sm text-muted-foreground">
              Contact your supervisor to create startup work instructions
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
          {checklists.map((checklist) => (
            <Card 
              key={checklist.id} 
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => setSelectedChecklist(checklist)}
            >
              <CardHeader>
                <CardTitle className="text-base">{checklist.name}</CardTitle>
                <Badge 
                  variant="outline" 
                  className="w-fit"
                  style={{ 
                    borderColor: getAssignmentLabel(checklist).color || undefined,
                    color: getAssignmentLabel(checklist).color || undefined
                  }}
                >
                  {getAssignmentLabel(checklist).label}
                </Badge>
                <CardDescription>
                  {checklist.description || `${checklist.checklist_items.length} items to complete`}
                </CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      )}

      {recentSubmissions.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-semibold">Recent Submissions</h3>
          <div className="space-y-2">
            {recentSubmissions.map((sub) => (
              <Card key={sub.id}>
                <CardContent className="py-3 px-4 flex items-center gap-3">
                  {sub.status === "completed" ? (
                    <CheckCircle2 className="w-5 h-5 text-success" />
                  ) : sub.status === "failed" ? (
                    <XCircle className="w-5 h-5 text-destructive" />
                  ) : (
                    <Clock className="w-5 h-5 text-warning" />
                  )}
                  <div className="flex-1">
                    <p className="font-medium">{sub.checklist?.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(sub.shift_date), "MMM d, yyyy")}
                    </p>
                  </div>
                  <Badge variant={sub.status === "completed" ? "default" : sub.status === "failed" ? "destructive" : "secondary"}>
                    {sub.status}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
